import { MessageDescriptor } from 'react-intl';
import { getParticipantRoles } from '../../api/activities-nim';
import { ActivityTypeCode, ListOfValuesReadDTO, ParticipantRoleCode } from '../../api/dto';
import { Locale } from '../../i18n/Locale';
import { getLovLabel } from '../../lib/label';
import { translateValue } from '../../lib/language';
import { FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { AdornableElement, AsyncAutocompleteFormElement, TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

const activityParticipantRoleTypeProperties = (
    { endAdornment }: AdornableElement,
    locale: Locale
): AsyncAutocompleteFormElement['typeProperties'] => ({
    idField: 'code',
    fetchOptions: (searchTerm, exactId, idField, values) =>
        getParticipantRoles({ activityType: (values?.[0] as ListOfValuesReadDTO | null | undefined)?.code ?? null }),
    watchPaths: ['type'],
    fetchUseCases: [FetchUseCase.ON_OPEN],
    getOptionLabel: (value: ListOfValuesReadDTO<ParticipantRoleCode>, intl, watchValues) => {
        // See OIM ParticipantRoleService#findListOfValues
        // This is probably anti-pattern, but hard to change it for the time being
        const visitRoles: Partial<Record<ParticipantRoleCode, MessageDescriptor['id']>> = {
            [ParticipantRoleCode.SUPERVISOR]: 'activity.value.participantRole.guide',
            [ParticipantRoleCode.EXECUTANT]: 'activity.value.participantRole.visitor',
        };
        const visitRoleKey =
            (watchValues![0] as ListOfValuesReadDTO<ActivityTypeCode>)?.code === ActivityTypeCode.GUIDED_VISIT
                ? visitRoles[value.code]
                : undefined;
        if (visitRoleKey !== undefined) {
            return intl.formatMessage({ id: visitRoleKey });
        } else {
            return getLovLabel(translateValue(value, locale));
        }
    },
    multiple: false,
    endAdornment,
});

interface ActivityParticipantRoleSchemaParameters
    extends Pick<AsyncAutocompleteFormElement, 'path' | 'label' | 'required'>,
        AdornableElement {}

export const activityParticipantRoleSchema = (
    { path, label, required, endAdornment }: ActivityParticipantRoleSchemaParameters,
    locale: Locale
): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    path,
    label,
    required,
    typeProperties: activityParticipantRoleTypeProperties({ endAdornment }, locale),
});
